import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import { useAuthUser } from '../../hooks/useAuthUser';
import { trackEvent } from '../../utils/analytics';
import logo from '../../assets/calcium-logo.svg';
import { MdChat, MdEmojiEvents, MdRestaurant, MdSmartToy } from 'react-icons/md';
import style from './OnboardingFlow.module.css';

const FEATURES = [
	{
		icon: <MdSmartToy />,
		title: 'Adaptive Workouts',
		description: 'Structured 30-minute sessions that adjust to your goals and schedule every week.'
	},
	{
		icon: <MdRestaurant />,
		title: 'Simple Meal Plans',
		description: 'No-fuss meals planned around your diet and updated automatically as you progress.'
	},
	{
		icon: <MdEmojiEvents />,
		title: 'XP, Streaks, Ranks',
		description: 'Built-in accountability that keeps you consistent without a personal trainer.'
	},
	{
		icon: <MdChat />,
		title: 'Built-In Community',
		description: 'Share wins, stay accountable, and connect with others on the same schedule.'
	}
];

const OnboardingIntro = () => {
	const { user } = useAuthUser();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	useEffect(() => {
		trackEvent('onboarding_view_screen1');
	}, []);

	const handleStart = () => {
		trackEvent('onboarding_click_start');
		navigate('/onboarding/personalize');
	};

	return (
		<div className={style.LandingPage}>
			<SEO
				title="30-minute AI workouts for busy professionals"
				description="Structured 30-minute home workouts and simple meal plans that adapt weekly. No planning required."
				path={pathname}
			/>

			<nav className={style.LandingNav}>
				<Link to="/" className={style.LandingLogoLink}>
					<img src={logo} alt="Calcium" className={style.LandingLogo} />
					<span className={style.LandingLogoText}>Calcium</span>
				</Link>
				<div className={style.LandingNavRight}>
					{user ? (
						<Link to="/" className={style.LandingNavLink}>Dashboard</Link>
					) : (
						<Link to={`/login?redirectTo=${encodeURIComponent('/')}`} className={style.LandingNavLink}>Log in</Link>
					)}
				</div>
			</nav>

			<div className={style.LandingHero}>
				<div className={style.HeroLogoWrap}>
					<div className={style.HeroGlassPulse} />
					<img src={logo} alt="Calcium" className={style.HeroLogo} />
				</div>
				<h1 className={style.HeroHeadline}>
					AI-Powered Fitness — Workouts, Meals, and Real Progress.
				</h1>
				<p className={style.HeroSub}>
					Personalized plans that adapt, with built-in accountability — all in one system.
				</p>
				<div className={style.HeroCTA}>
					<PrimaryButton
						text="Build my plan"
						click={handleStart}
						width="280px"
					/>
				</div>
				<div className={style.Microcopy}>
					<span>Takes 60 seconds</span>
					<span className={style.DividerDot} />
					<span>Cancel anytime</span>
				</div>
				<div className={style.ReturningUserRow}>
					<span>Returning user?</span>
					<Link to={`/login?redirectTo=${encodeURIComponent('/')}`} className={style.ReturningUserLink}>Log in here</Link>
				</div>
			</div>

			<div className={style.FeaturesGrid}>
				{FEATURES.map((feature) => (
					<div key={feature.title} className={style.FeatureCard}>
						<div className={style.FeatureIcon}>{feature.icon}</div>
						<h3 className={style.FeatureTitle}>{feature.title}</h3>
						<p className={style.FeatureDesc}>{feature.description}</p>
					</div>
				))}
			</div>
		</div>
	);
};

export default OnboardingIntro;
